<template>
  <div class="flex h-full min-h-0 w-full flex-col gap-4">
    <section
      class="flex shrink-0 flex-row flex-wrap items-end gap-3 rounded-lg border border-slate-200 p-4 dark:border-slate-600"
    >
      <div class="min-w-64 flex-1">
        <BaseShowEditDateRangePicker
          :start="from"
          :end="to"
          :label="$t('global.period')"
          :errors="errors.from ?? errors.to"
          name="period"
          edition-mode
          required
          @update:start="from = $event"
          @update:end="to = $event"
        />
      </div>
      <BaseButton
        icon="search"
        :display-loader="loading"
        :disabled="loading || !from || !to"
        :title="$t('button.search')"
        @click="search"
      />
    </section>

    <div class="flex min-h-0 flex-1 flex-col gap-2 overflow-y-auto pr-1">
      <LoadingComponent v-if="loading" size="small" :display-label="false" />
      <template v-else-if="searched">
        <label
          v-if="selectableDocuments.length"
          class="flex shrink-0 cursor-pointer items-center gap-2 px-1 text-sm font-medium text-primary"
        >
          <input type="checkbox" :checked="allSelected" @change="toggleAll" />
          <span class="capitalize-first">
            {{
              allSelected ? $t('button.unselect_all') : $t('button.select_all')
            }}
          </span>
        </label>
        <article
          v-for="document in documents"
          :key="document.id"
          class="flex flex-row gap-3 rounded-lg border p-3"
          :class="
            isSelectable(document)
              ? 'border-slate-200 dark:border-slate-600'
              : 'border-slate-100 bg-slate-50 text-gray-400 dark:border-slate-700 dark:bg-slate-800'
          "
        >
          <div class="w-5 shrink-0 pt-0.5">
            <input
              v-if="isSelectable(document)"
              v-model="selected"
              type="checkbox"
              :value="document.id"
            />
          </div>
          <div class="flex min-w-0 flex-1 flex-col gap-1">
            <div class="flex flex-row flex-wrap items-center gap-2">
              <span class="font-semibold">{{ document.reference }}</span>
              <span
                class="rounded bg-slate-100 px-1.5 py-0.5 text-xs dark:bg-slate-600"
              >
                {{ docTypeName(document.doc_type) }}
              </span>
              <span class="text-xs">{{ formatDate(document.date) }}</span>
            </div>
            <div v-if="document.deceased_name" class="text-sm">
              {{ $t('global.deceased') }} : {{ document.deceased_name }}
            </div>
            <div
              v-if="document.customer_company_name || document.responsible_name"
              class="text-xs"
            >
              {{ document.customer_company_name }}
              <template v-if="document.responsible_name">
                — {{ document.responsible_name }}
              </template>
            </div>
            <div
              v-if="document.converted_to_draft"
              class="mt-1"
              data-test="converted-to-draft"
            >
              <span
                class="w-fit rounded bg-amber-100 px-2 py-0.5 text-xs font-semibold text-amber-800 capitalize-first dark:bg-amber-900 dark:text-amber-200"
              >
                {{ $t('global.commission_converted_to_draft') }}
              </span>
            </div>
            <div
              v-if="document.commission === 'counted'"
              class="mt-1 text-xs italic"
            >
              {{
                $t('global.commission_document_counted_in', {
                  note: document.counted_in_note?.reference ?? ''
                })
              }}
            </div>
            <div
              v-else-if="document.commission === 'excluded'"
              class="mt-1 flex flex-row items-center gap-2"
            >
              <span
                class="rounded bg-red-100 px-2 py-0.5 text-xs font-semibold text-red-700"
              >
                {{ $t('global.commission_document_excluded') }}
              </span>
              <button
                type="button"
                class="text-xs font-medium text-primary hover:underline"
                @click="reinclude(document)"
              >
                {{ $t('button.reinclude') }}
              </button>
            </div>
          </div>
          <dl
            class="flex shrink-0 flex-col items-end gap-0.5 text-right text-xs"
          >
            <dt class="text-gray-500">{{ $t('attributes.total_htva') }}</dt>
            <dd class="font-semibold">
              <AmountFormatter :amount="document.total_htva" />
            </dd>
            <dt class="mt-1 text-gray-500">
              {{ $t('attributes.total_hors_debours') }}
            </dt>
            <dd>
              <AmountFormatter :amount="document.total_hors_debours ?? 0" />
            </dd>
          </dl>
        </article>
        <p v-if="!documents.length" class="py-6 text-center text-gray-500">
          {{ $t('global.no_results') }}
        </p>
      </template>
      <p v-if="errors.documents" class="text-sm text-red-600">
        {{ [].concat(errors.documents).join(' ') }}
      </p>
    </div>

    <div class="flex shrink-0 justify-end gap-3">
      <BaseButton
        theme-color="bg-secondary text-contrast-secondary"
        :disabled="adding"
        :title="$t('button.cancel')"
        @click="$emit('cancel')"
      />
      <BaseButton
        :disabled="adding || !selectedIds.length"
        :display-loader="adding"
        :title="
          $t('global.commission_note_add_selected', {
            count: selectedIds.length
          })
        "
        @click="add"
      />
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import AmountFormatter from '@c/AmountFormatter.vue'
import LoadingComponent from '@c/TheLoadingComponent.vue'
import BaseButton from '@c/addf-package/components/BaseButton/BaseButton.vue'
import BaseShowEditDateRangePicker from '@c/addf-package/components/BaseShowEditDatePicker/BaseShowEditDateRangePicker.vue'
import {
  defaultPeriod,
  isSelectable,
  selectedAvailableIds
} from '@/views/invoicing/show/commissionNote/components/commissionNoteCandidates'

/**
 * Adds invoices (also pro forma invoices and purchase orders) of the advisor
 * and agency of an open note: look at a period, tick the documents whose
 * commission is still available, add them in one go.
 */
export default {
  name: 'CommissionNoteDocumentsPicker',
  components: {
    AmountFormatter,
    BaseButton,
    BaseShowEditDateRangePicker,
    LoadingComponent
  },
  props: {
    commissionNoteId: { type: [String, Number], required: true },
    // the period of the note: the documents of that period are listed at once (up to today without an end)
    period: { type: Object, default: null }
  },
  emits: ['saved', 'cancel'],
  data() {
    return {
      ...this.initialPeriod(),
      documents: [],
      selected: [],
      searched: false,
      loading: false,
      searchRequest: 0,
      adding: false
    }
  },
  computed: {
    ...mapGetters({
      errors: 'auth/getErrors',
      docTypes: 'metadata/getInvoiceDocTypes'
    }),
    hasPeriod() {
      return !!this.period?.start
    },
    selectableDocuments() {
      return this.documents.filter(isSelectable)
    },
    selectedIds() {
      return selectedAvailableIds(this.documents, this.selected)
    },
    allSelected() {
      return (
        this.selectableDocuments.length > 0 &&
        this.selectedIds.length === this.selectableDocuments.length
      )
    }
  },
  created() {
    if (this.hasPeriod) {
      this.search()
    }
  },
  methods: {
    ...mapActions({
      processCandidatesAction: 'commissionNote/fetchCommissionNoteCandidates',
      processAddAction: 'commissionNote/addCommissionNoteDocuments',
      processReincludeAction: 'commissionNote/reincludeCommissionDocument'
    }),
    isSelectable,
    initialPeriod() {
      if (!this.period?.start) {
        return defaultPeriod()
      }

      return {
        from: this.period.start,
        to: this.period.end ?? defaultPeriod().to
      }
    },
    docTypeName(docType) {
      return this.docTypes?.find((type) => type.id === docType)?.name ?? docType
    },
    formatDate(date) {
      return date ? this.$h.formatDate(date, 'DD/MM/YYYY') : ''
    },
    toggleAll() {
      this.selected = this.allSelected
        ? []
        : this.selectableDocuments.map((document) => document.id)
    },
    search() {
      this.loading = true
      // only the answer of the last search is kept
      const request = (this.searchRequest += 1)
      this.processCandidatesAction({
        id: this.commissionNoteId,
        from: this.from,
        to: this.to
      })
        .then((response) => {
          if (request !== this.searchRequest) return
          this.documents = response.documents ?? []
          this.selected = []
          this.searched = true
        })
        .catch(() => {})
        .finally(() => {
          if (request === this.searchRequest) this.loading = false
        })
    },
    reinclude(document) {
      this.processReincludeAction({ invoiceId: document.id })
        .then(() => {
          document.commission = 'available'
        })
        .catch(() => {})
    },
    add() {
      this.adding = true
      this.processAddAction({
        id: this.commissionNoteId,
        documents: this.selectedIds
      })
        .then((response) => {
          this.$emit('saved', response.commission_note)
        })
        .catch(() => {})
        .finally(() => {
          this.adding = false
        })
    }
  }
}
</script>
